import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GenericListPage } from '../../components/generic';
import { usePackages, Package } from './useData';
import { moduleConfig, getColumns, getToolbarIcon, getTableActions } from './config';
import PackageTasksPanel from './PackageTasksPanel';
import { cn } from '../../lib/utils';

const SPLITTER_STORAGE_KEY = 'crm-packages-linked-tasks-splitter';
const SPLITTER_MIN = 40; // list min %
const SPLITTER_MAX = 85; // list max % (panel at least 15%)

const List: React.FC = () => {
  const [linkedTasksPanelOpen, setLinkedTasksPanelOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [splitterPosition, setSplitterPosition] = useState(() => {
    try {
      const v = sessionStorage.getItem(SPLITTER_STORAGE_KEY);
      if (v != null) {
        const n = Number(v);
        if (Number.isFinite(n) && n >= SPLITTER_MIN && n <= SPLITTER_MAX) return n;
      }
    } catch {
      // ignore
    }
    return 70;
  });
  const [isResizing, setIsResizing] = useState(false);
  const { packages, mutate } = usePackages();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hasRestoredFromParams, setHasRestoredFromParams] = useState(false);

  const handleSplitterMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const container = document.querySelector('.crm-packages-split-container');
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const newPosition = ((e.clientX - rect.left) / rect.width) * 100;
      setSplitterPosition(Math.max(SPLITTER_MIN, Math.min(SPLITTER_MAX, newPosition)));
    };
    const handleMouseUp = () => setIsResizing(false);
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  useEffect(() => {
    try {
      sessionStorage.setItem(SPLITTER_STORAGE_KEY, String(splitterPosition));
    } catch {
      // ignore
    }
  }, [splitterPosition]);

  const openLinkedTasksPanel = useCallback(
    (pkg: Package) => {
      setSelectedPackage(pkg);
      setLinkedTasksPanelOpen(true);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('linkedTasks', '1');
        next.set('packageId', String(pkg.id));
        return next;
      });
    },
    [setSearchParams]
  );

  const closeLinkedTasksPanel = useCallback(() => {
    setLinkedTasksPanelOpen(false);
    setSelectedPackage(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('linkedTasks');
      next.delete('packageId');
      return next;
    });
  }, [setSearchParams]);

  // Auto-open linked tasks panel when URL has the corresponding query params
  useEffect(() => {
    // Only auto-open once per mount based on URL params
    if (hasRestoredFromParams) return;

    const linkedFlag = searchParams.get('linkedTasks');
    const packageIdParam = searchParams.get('packageId');
    if (linkedFlag === '1' && packageIdParam && packages && packages.length > 0) {
      const pkgId = parseInt(packageIdParam, 10);
      const pkg = packages.find((p) => p.id === pkgId);
      if (pkg) {
        setSelectedPackage(pkg);
        setLinkedTasksPanelOpen(true);
        setHasRestoredFromParams(true);
      }
    }
  }, [searchParams, packages, hasRestoredFromParams]);

  return (
    <div className={cn('flex h-full min-h-0 relative', linkedTasksPanelOpen && 'crm-packages-split-container')}>
      <div
        className={cn('min-h-0 overflow-hidden', linkedTasksPanelOpen ? 'flex-shrink-0' : 'flex-1 min-w-0')}
        style={linkedTasksPanelOpen ? { width: `${splitterPosition}%` } : undefined}
      >
        <GenericListPage<Package>
          useData={usePackages}
          listContext={{ onOpenLinkedTasksPanel: openLinkedTasksPanel }}
          config={{
            ...moduleConfig,
            getColumns,
            getToolbarIcon,
            getTableActions,
          }}
        />
      </div>
      {linkedTasksPanelOpen && (
        <>
          <div
            className={cn(
              'absolute top-0 bottom-0 w-1 cursor-col-resize transition-colors z-10 shrink-0 opacity-50 hover:opacity-100 bg-gray-300 dark:bg-gray-600',
              isResizing && '!opacity-100'
            )}
            style={{ left: `${splitterPosition}%`, transform: 'translateX(-50%)' }}
            onMouseDown={handleSplitterMouseDown}
            role="separator"
            aria-label="Resize panels"
          >
            <div className="absolute inset-y-0 -left-1 -right-1" />
          </div>
          <div
            className="flex-shrink-0 flex flex-col min-h-0 min-w-0 overflow-hidden"
            style={{ width: `${100 - splitterPosition}%` }}
          >
            <PackageTasksPanel
              open
              onClose={closeLinkedTasksPanel}
              packageItem={selectedPackage}
              onTaskAdded={mutate}
              asPanel
            />
          </div>
        </>
      )}
    </div>
  );
};

export default List;

